import { json2csv } from 'json-2-csv';

/**
 * CSV/Excel export of rows the caller already holds, projected through an
 * explicit column list.
 *
 * This deliberately does NOT reuse `~/hooks/useGridExporter`. That hook reads
 * columns off a MUI `GridApiPremium` ref, so it can only export what a grid
 * shows.
 */

/** One exported column. `header` is what lands in the CSV header row. */
export type CsvColumn<TRow> = {
  header: string;
  value: (row: TRow) => string | number | null | undefined;
};

/**
 * Leading characters that make Excel/Sheets parse a cell as a formula rather
 * than text. Tab and CR are included because both are stripped to nothing on
 * import, exposing whatever follows them as the real leading character.
 */
const FORMULA_TRIGGERS = /^[=+\-@\t\r]/;

/**
 * A cell that is entirely a signed number literal. `-42` and `+1.5e3` trip the
 * trigger test but cannot execute anything, and prefixing them would turn a
 * sortable number into text — which is the whole point of exporting to a
 * spreadsheet. Anything else leading with a sign (`-2+3`, `+cmd|...`) is not a
 * plain number and still gets neutralized.
 */
const PLAIN_NUMBER = /^[-+]?\d+(\.\d+)?([eE][-+]?\d+)?$/;

/**
 * Neutralize spreadsheet formula injection.
 *
 * Exported strings are not trusted constants: vendor names, property names and
 * ticket titles originate from ingested documents, and the Markdown-table path
 * reads both cells *and* column headers straight out of model-authored output.
 * A value like `=HYPERLINK("http://evil","click")` is inert in the CSV itself
 * but executes when a consultant opens the file — and these files exist to be
 * emailed to clients.
 *
 * The leading apostrophe is the standard mitigation: spreadsheets treat it as
 * "the rest of this cell is literal text" and do not render it, so the value
 * the user reads is unchanged while the parser never sees a formula.
 */
function neutralizeFormula(value: string): string {
  if (!FORMULA_TRIGGERS.test(value)) return value;
  if (PLAIN_NUMBER.test(value)) return value;
  return `'${value}`;
}

/** Quote a field that would otherwise break the header row's comma framing. */
function escapeCsvField(field: string): string {
  return /[",\r\n]/.test(field) ? `"${field.replace(/"/g, '""')}"` : field;
}

/**
 * Neutralized, de-duplicated column keys.
 *
 * Records are keyed by header, so two identically-named columns would collapse
 * into one and silently drop a column of data. Widget paths pass hardcoded
 * literals and can't collide, but the Markdown path reads headers out of
 * model-authored DOM, where a repeated header is entirely reachable.
 */
function resolveHeaders<TRow>(columns: CsvColumn<TRow>[]): string[] {
  const taken = new Set<string>();
  return columns.map((col) => {
    const header = neutralizeFormula(col.header);
    // Step the suffix until the candidate is genuinely unused. Checking only
    // the raw header would still collide when a *generated* name matches a
    // later literal one — ['Total', 'Total', 'Total (2)'] would otherwise
    // produce 'Total (2)' twice and collapse a column anyway.
    let candidate = header;
    let suffix = 1;
    while (taken.has(candidate)) {
      suffix += 1;
      candidate = `${header} (${suffix})`;
    }
    taken.add(candidate);
    return candidate;
  });
}

/**
 * Rows are projected through an explicit column list rather than
 * `Object.keys(row)`. The widget DTOs carry internal ids (`InvoiceCardData.id`
 * et al) that exist for click-through navigation and have no business in a
 * file a consultant emails to a client. An allow-list means adding a field to
 * a card cannot silently add it to the export.
 */
export function rowsToCsv<TRow>(rows: TRow[], columns: CsvColumn<TRow>[]): string {
  const headers = resolveHeaders(columns);
  const records = rows.map((row) =>
    Object.fromEntries(
      columns.map((col, index) => {
        const raw = col.value(row);
        const cell = raw === null || raw === undefined ? '' : raw;
        // Numbers are passed through as-is: they cannot carry a formula, and
        // stringifying them here would cost the spreadsheet its sort order.
        return [headers[index], typeof cell === 'string' ? neutralizeFormula(cell) : cell];
      }),
    ),
  );
  // Emit the header row even when there are no records, so an empty export is
  // still a valid CSV rather than a zero-byte file.
  if (records.length === 0) {
    return headers.map(escapeCsvField).join(',');
  }
  return json2csv(records);
}

/** Slugify a widget title into a safe filename stem. */
export function toFilename(title: string, fallback = 'export'): string {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  return slug || fallback;
}

function downloadBlob(blob: Blob, filename: string): void {
  if (!('download' in HTMLAnchorElement.prototype)) return;

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url));
}

function downloadCsv(csv: string, filename: string): void {
  // Prepended BOM so Excel opens UTF-8 accents correctly — these exports are
  // sent to clients, and "Ø" in a vendor name is a support ticket.
  const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(blob, `${filename}.csv`);
}

export function exportRowsAsCsv<TRow>(
  rows: TRow[],
  columns: CsvColumn<TRow>[],
  title: string,
): void {
  downloadCsv(rowsToCsv(rows, columns), toFilename(title));
}

/**
 * No formula neutralizing here, unlike the CSV path: exceljs writes strings as
 * text cells, which a spreadsheet never evaluates, so a leading apostrophe
 * would only show up as a stray character.
 */
export async function exportRowsAsExcel<TRow>(
  rows: TRow[],
  columns: CsvColumn<TRow>[],
  title: string,
): Promise<void> {
  const Excel = await import('exceljs');
  const workbook = new Excel.Workbook();
  const worksheet = workbook.addWorksheet('Sheet1');

  worksheet.addRow(columns.map((col) => col.header));
  rows.forEach((row) => worksheet.addRow(columns.map((col) => col.value(row) ?? '')));

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  downloadBlob(blob, `${toFilename(title)}.xlsx`);
}
